import { Capacitor } from "@capacitor/core";
import { Box, Dialog, DialogContent, DialogTitle, Divider, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { FC } from "react";
import { useTranslation } from "react-i18next";
import { INK_BLACK, STATUS, SURFACE } from "@/theme/tokens";
import { APP_STORE_URL, AppPlatform, PLAY_STORE_URL } from "@/utils/models";
import { EnforcementMode } from "@/utils/versionCheck";

interface ForceUpdateScreenProps {
	mode: EnforcementMode.SOFT | EnforcementMode.HARD;
	onDismiss?: () => void;
}

export const ForceUpdateScreen: FC<ForceUpdateScreenProps> = ({ mode, onDismiss }) => {
	const { t } = useTranslation();
	const platform = Capacitor.getPlatform();
	const storeUrl = platform === AppPlatform.IOS ? APP_STORE_URL : PLAY_STORE_URL;
	const isSoft = mode === EnforcementMode.SOFT;

	return (
		<Dialog
			open
			slotProps={{
				backdrop: {
					sx: {
						bgcolor: alpha(INK_BLACK, 0.4),
						backdropFilter: "blur(8px)",
						WebkitBackdropFilter: "blur(8px)",
					},
				},
				paper: {
					sx: {
						borderRadius: "14px",
						maxWidth: 270,
						mx: "auto",
						overflow: "hidden",
						bgcolor: alpha(SURFACE, 0.95),
					},
				},
			}}
		>
			<DialogTitle sx={{ textAlign: "center", fontSize: "17px", fontWeight: 600, letterSpacing: "-0.2px", pb: 0.5 }}>
				{mode === EnforcementMode.HARD ? t("app.update.required.title") : t("app.update.available.title")}
			</DialogTitle>
			<DialogContent sx={{ textAlign: "center", px: 2, pb: 2 }}>
				<Typography sx={{ fontSize: "13px", color: "text.secondary", lineHeight: 1.4 }}>{t("app.update.default.message")}</Typography>
			</DialogContent>

			<Divider />

			<Box sx={{ display: "flex" }}>
				{isSoft && (
					<>
						<Box
							component="a"
							onClick={onDismiss}
							sx={{
								flex: 1,
								py: 1.4,
								textAlign: "center",
								fontSize: "17px",
								color: STATUS.ERROR.main,
								cursor: "pointer",
								textDecoration: "none !important",
								userSelect: "none",
								"&:active": { bgcolor: alpha(INK_BLACK, 0.05) },
							}}
						>
							{t("app.update.later")}
						</Box>
						<Divider orientation="vertical" flexItem />
					</>
				)}
				<Box
					component="a"
					href={storeUrl}
					target="_blank"
					rel="noopener noreferrer"
					sx={{
						flex: 1,
						py: 1.4,
						textAlign: "center",
						fontSize: "17px",
						fontWeight: 600,
						color: STATUS.INFO.main,
						cursor: "pointer",
						textDecoration: "none !important",
						userSelect: "none",
						"&:active": { bgcolor: alpha(INK_BLACK, 0.05) },
					}}
				>
					{t("app.update.button")}
				</Box>
			</Box>
		</Dialog>
	);
};
